import { useState } from 'react';

import {
  Button,
  Circle,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';

import { IconCloudUploadBrand } from '@/components/Icons';
import { Link } from '@/components/Link';
import { UploadDropZone } from '@/components/UploadDropZone';
import { useUploadFileMutation } from '@/features/file';

import { DuplicatedEmailsModal } from '../candidate/DuplicatedEmailsModal';
import { useAddSurveyParticipantExcelMutation } from './useAdminSurveyQueries';

type ImportExcelModalProps = Omit<ModalProps, 'children'> & {
  surveyId: string;
};

export function ImportParticipantsExcelModal({
  surveyId,
  ...props
}: ImportExcelModalProps) {
  const { t } = useTranslation(['candidate']);
  const fileUploadMutation = useUploadFileMutation();
  const [file, setFile] = useState<File | null>(null);

  const [duplicatedEmails, setDuplicatedEmails] = useState<Array<string>>([]);
  const {
    isOpen: isDuplicatedEmailsModalOpen,
    onOpen: onDuplicatedEmailsModalOpen,
    onClose: onDuplicatedEmailsModalClose,
  } = useDisclosure();

  const importExcelMutation = useAddSurveyParticipantExcelMutation({
    onSuccess: (data) => {
      if (data.duplicatedEmails && data.duplicatedEmails.length > 0) {
        setDuplicatedEmails(data.duplicatedEmails);
        onDuplicatedEmailsModalOpen();
      }
    },
  });

  const submitFile = async (file: Blob, surveyId: string) => {
    if (!surveyId) {
      return;
    }

    const uploadRes = await fileUploadMutation.mutateAsync({
      access: {
        contentType: file.type,
      },
      file,
    });

    await importExcelMutation.mutateAsync({
      surveyId: surveyId,
      body: {
        fileUrl: uploadRes.fileKey,
      },
    });

    props.onClose();
    setFile(null);
  };

  const handleImportWitExcel = () => {
    submitFile(file!, surveyId);
  };

  return (
    <>
      <Modal {...props} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalCloseButton />
          </ModalHeader>

          <ModalBody>
            <VStack gap={2}>
              <Heading lineHeight={1} fontSize="xl" textAlign="center">
                {t('candidate:importByExcel.title')}
              </Heading>
              <Text mb={4} fontSize="sm" textAlign="center">
                {t('candidate:importByExcel.description')}
              </Text>

              <UploadDropZone
                w="full"
                cursor="pointer"
                onDrop={([file]) => {
                  if (file) setFile(file);
                }}
                dropZoneOptions={{
                  accept: {
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                      ['.xlsx'],
                  },
                  multiple: false,
                }}
              >
                <Circle size="40px" bg="brand.50" fontSize={20}>
                  <IconCloudUploadBrand />
                </Circle>
                {file ? (
                  <Text fontSize="sm">{file.name}</Text>
                ) : (
                  <Trans
                    t={t}
                    parent={(props) => <Text textAlign="center" {...props} />}
                    i18nKey="candidate:importByExcel.fileInstruction"
                    components={[
                      <Text color="brand.700" as="span" key="0" />,
                      <Text as="span" key="1" />,
                      <Text fontSize="sm" key="2" />,
                    ]}
                  />
                )}
              </UploadDropZone>
              <Trans
                t={t}
                parent={(props) => (
                  <Text mt={3} fontSize="xs" textAlign="center" {...props} />
                )}
                i18nKey="candidate:importByExcel.exampleFile"
                components={{
                  Link: (
                    <Link
                      color="brand.700"
                      to="https://cdn.synapsai.ai/excel/synapsai_survey_participation_example.xlsx"
                    />
                  ),
                }}
              />
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              isDisabled={!file}
              variant="@primary"
              w="full"
              onClick={handleImportWitExcel}
              isLoading={
                importExcelMutation.isPending || fileUploadMutation.isPending
              }
            >
              {t('candidate:importByExcel.upload')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <DuplicatedEmailsModal
        isOpen={isDuplicatedEmailsModalOpen}
        onClose={onDuplicatedEmailsModalClose}
        duplicatedEmails={duplicatedEmails}
      />
    </>
  );
}
